import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Badge,
  Form,
} from "react-bootstrap";
import {
  useGetInactiveVendorProductsQuery,
  useDeleteProductMutation,
  useGetVendorCategorySubcategoryMapQuery,
} from "../slices/productApiSlice";
import { useNavigate } from "react-router-dom";

const InactiveVendorProductListScreen = () => {
  const navigate = useNavigate();

  // Local state for search
  const [keyword, setKeyword] = useState("");

  // Fetch inactive products
  const {
    data: products,
    isLoading,
    error,
    refetch,
  } = useGetInactiveVendorProductsQuery(keyword);

  const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation();

  const {
    data: categoryMap,
    isLoading: isCategoryMapLoading,
    error: categoryMapError,
  } = useGetVendorCategorySubcategoryMapQuery();

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");

  const categoryList =
    categoryMap?.categorySubcategoryList
      ? Object.entries(categoryMap.categorySubcategoryList).flatMap(
          ([category, subcategories]) =>
            subcategories.map((subcategory) => ({ category, subcategory }))
        )
      : [];

  const uniqueCategories = [...new Set(categoryList.map((item) => item.category))];

  // Filter products locally by category/subcategory (search done via RTK query)
  const filteredProducts = products?.filter((item) => {
    if (!selectedCategory && !selectedSubcategory) return true;
    if (selectedCategory && !selectedSubcategory) return item.category === selectedCategory;
    if (selectedCategory && selectedSubcategory)
      return (
        item.category === selectedCategory &&
        item.subcategory === selectedSubcategory
      );
    return true;
  });

  const handleDelete = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId).unwrap();
        refetch();
      } catch (err) {
        alert(err?.data?.message || "❌ Failed to delete product");
      }
    }
  };

  if (isLoading || isDeleting || isCategoryMapLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  if (error || categoryMapError) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <Alert variant="danger">
          {error?.data?.message || categoryMapError?.data?.message || "Failed to load data"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Inactive Products</h2>

      {/* 🔍 Search & Filters */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search products..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </Col>

        <Col md={4}>
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setSelectedSubcategory("");
            }}
          >
            <option value="">All Categories</option>
            {uniqueCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </Col>

        <Col md={4}>
          <select
            className="form-select"
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
            disabled={!selectedCategory}
          >
            <option value="">All Subcategories</option>
            {categoryList
              .filter((item) => item.category === selectedCategory)
              .map((item) => (
                <option key={item.subcategory} value={item.subcategory}>
                  {item.subcategory}
                </option>
              ))}
          </select>
        </Col>
      </Row>

      {/* 🛍️ Product List */}
      <Row>
        {filteredProducts?.length > 0 ? (
          filteredProducts.map((item) => (
            <Col key={item._id} sm={12} md={6} lg={4} className="mb-3">
              <Card className="shadow-sm h-100">
                {item.images?.length > 0 && (
                  <Card.Img
                    variant="top"
                    src={item.images[0]}
                    alt={item.name}
                    style={{ height: "200px", objectFit: "cover" }}
                  />
                )}

                <Card.Body>
                  <Card.Title className="d-flex justify-content-between align-items-center">
                    {item.name} - {item.Tamil}
                    {item.discount ? (
                      <Badge bg="warning" text="dark">
                        {item.discount}% Off
                      </Badge>
                    ) : null}
                  </Card.Title>

                  <Card.Text>
                    <small className="text-muted">{item.description}</small>
                    <br />
                    <strong>Price:</strong> ₹{item.price?.toFixed(2)} <br />
                    <strong>Category:</strong> {item.category} <br />
                    <strong>Subcategory:</strong> {item.subcategory} <br />
                    <strong>Vendor:</strong> {item.vendorName || "Unknown"} <br />
                    <strong>Created At:</strong> {new Date(item.createdAt).toLocaleDateString()} <br />
                  </Card.Text>

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => navigate(`/edit-product/${item._id}`)}
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(item._id)}
                    >
                      <i className="bi bi-trash"></i> Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          <Col>
            <Alert variant="info">No inactive products found.</Alert>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default InactiveVendorProductListScreen;
